import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { FetchDataArg } from '../utils/fetch';

export const fetchForecastData = createAsyncThunk(
  'forecast/fetchData',
  async (arg: FetchDataArg) => {
    return await arg.fetch<{
      data: AllEntities;
    }>(arg.endpoint, arg.options);
  }
);

type AllEntities =
  | HourlyEntities
  | DailyEntities
  | MonthlyEntities
  | YearlyEntities;
type DataSliceType<T> = GridusResponseType<{ data: T }>;

const initialState: SliceInitialState<{ data: AllEntities }> = {
  data: null,
  loading: false,
  error: null,
  lastFetchTime: null,
};

const forecastSlice = createSlice({
  name: 'forecast/fetchData',
  initialState: initialState,
  reducers: {
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchForecastData.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchForecastData.fulfilled,
        (
          state,
          action: {
            payload: DataSliceType<AllEntities> | GridusResponseErrorType;
          }
        ) => {
          state.loading = false;
          state.lastFetchTime = Date.now();
          if (action.payload.status === 'success') {
            state.error = null;
            state.data = action.payload as DataSliceType<AllEntities>;
          } else {
            state.data = null;
            state.error = action.payload as GridusResponseErrorType;
          }
        }
      )
      .addCase(fetchForecastData.rejected, (state, action) => {
        state.loading = false;
        state.lastFetchTime = Date.now();
        state.data = null;
        state.error = action.error as GridusResponseErrorType;
      });
  },
});

export const fetchForecastPeriodData = createAsyncThunk(
  'forecast/period/fetchData',
  async (arg: FetchDataArg) => {
    return await arg.fetch<PeriodEntity>(arg.endpoint, arg.options);
  }
);

const initialPeriodState: SliceInitialState<PeriodEntity> = {
  data: null,
  loading: false,
  error: null,
  lastFetchTime: null,
};

const forecastPeriodSlice = createSlice({
  name: 'forecast/period/fetchData',
  initialState: initialPeriodState,
  reducers: {
    resetState: () => initialPeriodState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchForecastPeriodData.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchForecastPeriodData.fulfilled,
        (
          state,
          action: {
            payload: GridusResponseType<PeriodEntity> | GridusResponseErrorType;
          }
        ) => {
          state.loading = false;
          state.lastFetchTime = Date.now();
          if (action.payload.status === 'success') {
            state.error = null;
            state.data = action.payload as GridusResponseType<PeriodEntity>;
          } else {
            state.data = null;
            state.error = action.payload as GridusResponseErrorType;
          }
        }
      )
      .addCase(fetchForecastPeriodData.rejected, (state, action) => {
        state.loading = false;
        state.lastFetchTime = Date.now();
        state.data = null;
        state.error = action.error as GridusResponseErrorType;
      });
  },
});

export const fetchForecastTodayData = createAsyncThunk(
  'forecast/today/fetchData',
  async (arg: FetchDataArg) => {
    return await arg.fetch<{ data: DailyEntities }>(arg.endpoint, arg.options);
  }
);

const initialForecastToday: SliceInitialState<{ data: DailyEntities }> = {
  data: null,
  loading: false,
  error: null,
  lastFetchTime: null,
};

const forecastTodaySlice = createSlice({
  name: 'forecast/today/fetchData',
  initialState: initialForecastToday,
  reducers: {
    resetState: () => initialForecastToday,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchForecastTodayData.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchForecastTodayData.fulfilled,
        (
          state,
          action: {
            payload:
              | GridusResponseType<{ data: DailyEntities }>
              | GridusResponseErrorType;
          }
        ) => {
          state.loading = false;
          state.lastFetchTime = Date.now();
          if (action.payload.status === 'success') {
            state.error = null;
            state.data = action.payload as GridusResponseType<{
              data: DailyEntities;
            }>;
          } else {
            state.data = null;
            state.error = action.payload as GridusResponseErrorType;
          }
        }
      )
      .addCase(fetchForecastTodayData.rejected, (state, action) => {
        state.loading = false;
        state.lastFetchTime = Date.now();
        state.data = null;
        state.error = action.error as GridusResponseErrorType;
      });
  },
});

export const fetchForecastTommorowData = createAsyncThunk(
  'forecast/tommorow/fetchData',
  async (arg: FetchDataArg) => {
    return await arg.fetch<{ data: DailyEntities }>(arg.endpoint, arg.options);
  }
);

const initialForecastTommorow: SliceInitialState<{ data: DailyEntities }> = {
  data: null,
  loading: false,
  error: null,
  lastFetchTime: null,
};

const forecastTommorowSlice = createSlice({
  name: 'forecast/tommorow/fetchData',
  initialState: initialForecastTommorow,
  reducers: {
    resetState: () => initialForecastTommorow,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchForecastTommorowData.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchForecastTommorowData.fulfilled,
        (
          state,
          action: {
            payload:
              | GridusResponseType<{ data: DailyEntities }>
              | GridusResponseErrorType;
          }
        ) => {
          state.loading = false;
          state.lastFetchTime = Date.now();
          if (action.payload.status === 'success') {
            state.error = null;
            state.data = action.payload as GridusResponseType<{
              data: DailyEntities;
            }>;
          } else {
            state.data = null;
            state.error = action.payload as GridusResponseErrorType;
          }
        }
      )
      .addCase(fetchForecastTommorowData.rejected, (state, action) => {
        state.loading = false;
        state.lastFetchTime = Date.now();
        state.data = null;
        state.error = action.error as GridusResponseErrorType;
      });
  },
});

export const fetchForecastCurrentMonthData = createAsyncThunk(
  'forecast/currentMonth/fetchData',
  async (arg: FetchDataArg) => {
    return await arg.fetch<{ data: MonthlyEntities }>(
      arg.endpoint,
      arg.options
    );
  }
);

const initialForecastCurrentMonth: SliceInitialState<{
  data: MonthlyEntities;
}> = {
  data: null,
  loading: false,
  error: null,
  lastFetchTime: null,
};

const forecastCurrentMonthSlice = createSlice({
  name: 'forecast/currentMonth/fetchData',
  initialState: initialForecastCurrentMonth,
  reducers: {
    resetState: () => initialForecastCurrentMonth,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchForecastCurrentMonthData.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchForecastCurrentMonthData.fulfilled,
        (
          state,
          action: {
            payload:
              | GridusResponseType<{ data: MonthlyEntities }>
              | GridusResponseErrorType;
          }
        ) => {
          state.loading = false;
          state.lastFetchTime = Date.now();
          if (action.payload.status === 'success') {
            state.error = null;
            state.data = action.payload as GridusResponseType<{
              data: MonthlyEntities;
            }>;
          } else {
            state.data = null;
            state.error = action.payload as GridusResponseErrorType;
          }
        }
      )
      .addCase(fetchForecastCurrentMonthData.rejected, (state, action) => {
        state.loading = false;
        state.lastFetchTime = Date.now();
        state.data = null;
        state.error = action.error as GridusResponseErrorType;
      });
  },
});

export const { resetState } = forecastSlice.actions;
export const { resetState: resetPeriodState } = forecastPeriodSlice.actions;
export const { resetState: resetTodayState } = forecastTodaySlice.actions;
export const { resetState: resetTomorrowState } = forecastTommorowSlice.actions;
export const { resetState: resetCurrentMonthState } =
  forecastCurrentMonthSlice.actions;
const { reducer } = forecastSlice;
const { reducer: periodReducer } = forecastPeriodSlice;
const { reducer: todayReducer } = forecastTodaySlice;
const { reducer: tommorowReducer } = forecastTommorowSlice;
const { reducer: currentMonthReducer } = forecastCurrentMonthSlice;
export {
  reducer as forecastReducer,
  periodReducer as forecastPeriodReducer,
  todayReducer as forecastTodayReducer,
  tommorowReducer as forecastTommorowReducer,
  currentMonthReducer as forecastCurrentMonthReducer,
};
